import { useCallback } from "react";

import { Trans } from "@lingui/react/macro";
import { useSession } from "@pontimed/auth-client";
import { createFileRoute, useNavigate } from "@tanstack/react-router";

import { AccountSecurityBanner } from "@/components/account/account-security-banner";
import { MessagingSurface } from "@/components/messaging/messaging-surface";

const UUID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

interface MessagesSearch {
  /** Open conversation — keeps deep links and the back button honest. */
  thread?: string;
}

export const Route = createFileRoute("/_authenticated/account/user/messages")({
  validateSearch: (search: Record<string, unknown>): MessagesSearch => ({
    // Thread ids are UUIDs; anything else would be sent to the API (which does
    // no shape check) every poll and 500 with a Prisma error each time.
    thread:
      typeof search.thread === "string" && UUID_RE.test(search.thread) ? search.thread : undefined,
  }),
  component: MessagesPage,
});

/**
 * Personal inbox. This is the destination the API already points message
 * notifications at (`link: "/account/user/messages"` in
 * apps/api/src/routes/account-messaging.ts) — until now the route did not
 * exist and patients had no way to read what a clinic sent them.
 */
function MessagesPage() {
  const { user } = useSession();
  const { thread } = Route.useSearch();
  const navigate = useNavigate();

  const setThread = useCallback(
    (threadId: string | null) => {
      void navigate({
        to: "/account/user/messages",
        // `undefined` drops the param from the URL entirely.
        search: { thread: threadId ?? undefined },
        replace: true,
      });
    },
    [navigate],
  );

  return (
    <div className="space-y-5">
      <header>
        <h1 className="font-display text-foreground text-2xl font-semibold tracking-[-0.02em]">
          <Trans>Messages</Trans>
        </h1>
        <p className="text-muted-foreground mt-1 text-sm">
          <Trans>Conversations with your doctors and clinics.</Trans>
        </p>
      </header>

      {/* Fixed height so the conversation scrolls inside its own pane instead
          of stretching the page. The offsets cover the app chrome (header +
          bottom tab bar + safe area), this page's title block, and the
          security banner underneath; `min-h` keeps it usable on short
          screens even if that means the page itself scrolls a little. */}
      <MessagingSurface
        currentUserId={user?.userId ?? ""}
        activeThreadId={thread ?? null}
        onActiveThreadChange={setThread}
        className="h-[calc(100dvh-16rem)] min-h-[26rem] lg:h-[calc(100dvh-13rem)]"
      />

      <AccountSecurityBanner />
    </div>
  );
}
